// src/AdminPollsPage.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, deleteDoc, doc, addDoc } from 'firebase/firestore';
import { db } from './firebase.js';
import moment from 'moment';

const AdminPollsPage = ({ user, isAdmin, isMobile }) => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newPoll, setNewPoll] = useState({
    question: '',
    options: ['', ''],
    endTime: moment().add(7, 'days').format('YYYY-MM-DDTHH:mm')
  });

  useEffect(() => {
    console.log('AdminPollsPage mounted. isAdmin:', isAdmin, 'isMobile:', isMobile);
    if (!isAdmin) {
      setError('Access denied');
      setLoading(false);
      console.log('Access denied - not admin');
      return;
    }

    const fetchPolls = async () => {
      console.log('Fetching polls...');
      try {
        const q = query(collection(db, 'polls'), orderBy('startTime', 'desc'));
        const snapshot = await getDocs(q);
        console.log('Polls snapshot size:', snapshot.size);
        const pollsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Fetched polls data:', pollsData);
        setPolls(pollsData);
      } catch (err) {
        console.error('Error fetching polls:', err);
        setError('Failed to load polls: ' + err.message);
      } finally {
        setLoading(false);
        console.log('Fetch complete. Loading set to false.');
      }
    };

    fetchPolls();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleCreatePoll = async () => {
    console.log('Creating poll with data:', newPoll);
    if (!newPoll.question.trim()) {
      alert('Question is required');
      return;
    }
    const filteredOptions = newPoll.options.filter(opt => opt.trim() !== '');
    if (filteredOptions.length < 2) {
      alert('At least 2 non-empty options required');
      return;
    }
    const endDate = moment(newPoll.endTime);
    if (!endDate.isValid()) {
      alert('Invalid end date');
      return;
    }
    if (endDate.isBefore(moment())) {
      alert('End date must be in the future');
      return;
    }
    try {
      await addDoc(collection(db, 'polls'), {
        question: newPoll.question,
        options: filteredOptions.map(text => ({ text, votes: 0 })),
        startTime: new Date(),
        endTime: endDate.toDate(),
        status: 'open',
        createdBy: user.uid
      });
      alert('Poll created successfully');
      // Refresh polls
      const q = query(collection(db, 'polls'), orderBy('startTime', 'desc'));
      const snapshot = await getDocs(q);
      const updatedPolls = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log('Refreshed polls:', updatedPolls);
      setPolls(updatedPolls);
    } catch (error) {
      console.error('Error creating poll:', error);
      alert('Failed to create poll: ' + error.message);
    }
  };

  const handleDeletePoll = async (pollId) => {
    if (window.confirm('Are you sure you want to delete this poll?')) {
      console.log('Deleting poll:', pollId);
      try {
        await deleteDoc(doc(db, 'polls', pollId));
        const updatedPolls = polls.filter(p => p.id !== pollId);
        console.log('Updated polls after delete:', updatedPolls);
        setPolls(updatedPolls);
        alert('Poll deleted successfully');
      } catch (error) {
        console.error('Error deleting poll:', error);
        alert('Failed to delete poll: ' + error.message);
      }
    }
  };

  const handleInputChange = (e) => {
    setNewPoll({ ...newPoll, [e.target.name]: e.target.value });
  };

  const addOption = () => {
    setNewPoll({ ...newPoll, options: [...newPoll.options, ''] });
  };

  const updateOption = (index, value) => {
    const updatedOptions = [...newPoll.options];
    updatedOptions[index] = value;
    setNewPoll({ ...newPoll, options: updatedOptions });
  };

  if (loading) return <p style={{ color: 'black', fontSize: '18px' }}>Loading polls...</p>;
  if (error) return <p style={{ color: 'red', fontSize: '18px' }}>{error}</p>;
  console.log('Rendering create form');
  console.log('Rendering polls list with', polls.length, 'items');
  return (
    <div style={{ padding: '20px', backgroundColor: '#f9f9f9', color: 'black', fontFamily: 'Arial, sans-serif' }}>
      <h2 style={{ color: '#333', fontSize: '24px' }}>Manage Polls</h2>
      <div style={{ marginBottom: '20px', border: '1px solid #ccc', padding: '10px' }}>
        <input
          type="text"
          name="question"
          placeholder="Poll Question"
          value={newPoll.question}
          onChange={handleInputChange}
          style={{ marginBottom: '10px', width: '100%', padding: '8px', fontSize: '16px' }}
        />
        {newPoll.options.map((opt, index) => (
          <input
            key={index}
            type="text"
            placeholder={`Option ${index + 1}`}
            value={opt}
            onChange={(e) => updateOption(index, e.target.value)}
            style={{ marginBottom: '10px', width: '100%', padding: '8px', fontSize: '16px' }}
          />
        ))}
        <button onClick={addOption} style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#2196F3', color: 'white', fontSize: '16px' }}>Add Option</button>
        <input
          type="datetime-local"
          name="endTime"
          value={newPoll.endTime}
          onChange={handleInputChange}
          style={{ marginBottom: '10px', width: '100%', padding: '8px', fontSize: '16px' }}
        />
        <button onClick={handleCreatePoll} style={{ padding: '10px', backgroundColor: '#4CAF50', color: 'white', fontSize: '16px' }}>Create Poll</button>
      </div>
      <div style={{ border: '1px solid #ccc', padding: '10px' }}>
        {polls.length === 0 ? (
          <p style={{ color: 'black', fontSize: '18px' }}>No polls available at this time.</p>
        ) : (
          polls.map(poll => (
            <div key={poll.id} style={{ border: '1px solid #ddd', padding: '10px', marginBottom: '10px', backgroundColor: 'white' }}>
              <h3 style={{ color: '#333', fontSize: '20px' }}>{poll.question}</h3>
              <p style={{ color: '#666', fontSize: '16px' }}>Ends: {moment(poll.endTime.toDate()).format('MMM D, YYYY h:mm a')}</p>
              <button onClick={() => handleDeletePoll(poll.id)} style={{ padding: '5px', backgroundColor: '#f44336', color: 'white', fontSize: '16px' }}>Delete</button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminPollsPage;